// src/encounter/medication-admin-requests.tsx
// MIDDLE-column "Medication Administration Requests" section (PROD-1524). React port of Flutter
// pre_encounter_widget.dart medicationAdministrationRequest() (:1595) + ActiveMedicationRow
// (active_medication_row_widget.dart): a section header + one row per A/P medication whose
// frequency == "now" (title · optional sig/route detail · "Requested on {date} by {name}" · a Give
// action). The nurse "Give"s the med — opening + saving the administration editor is the wiring seam
// kept out of this presentational host (parent owns the write).
//
// PRESENTATIONAL: takes the mapped view models (to-medication-admin-requests). HIDDEN when there are
// no requests (Flutter body() gate: length>0 ? Column : Container()). DS atoms + tokens only — no
// hardcoded hex, no invented chrome. Sibling of the shipped VaccineAdministrationRequests section.
import { Button, EncounterSectionHeader } from '@scripsteam/scrips-signal-ds';
import type { MedicationAdminRequestView } from './lib/to-medication-admin-requests';
import { useDesignV2 } from './lib/design-v2';
import { EncBlock, EncSectionCard } from './enc-section-card';

export interface MedicationAdminRequestsProps {
  requests: MedicationAdminRequestView[];
  /** Administer ("Give") a medication request. Omitted → rows render display-only (no action
   *  button), so the host call-site need not wire the administration write yet. */
  onGive?: (req: MedicationAdminRequestView) => void;
}

const CARD =
  'bg-surface-card border border-border-default ' +
  'rounded-lg p-6';
const ROW =
  'flex items-center justify-between gap-3 p-3 ' +
  // Medications ride the medications rail in Flutter (medicationsColor accent); map to a brand
  // accent token (no hardcoded hex). `--color-surface-subtle` has no DS token today, so the
  // fallback the old inline style leaned on is inlined directly as the surface-card colour.
  'border-l-3 border-l-brand-primary rounded-md ' +
  'bg-surface-card';
const TITLE = 'text-size-sm font-semibold text-text-primary';
const DETAIL = 'mt-1 text-size-xs text-text-secondary';
const META = 'mt-1 text-size-xs text-text-hint';
const STACK_2 = 'flex flex-col gap-2';

/** no-Z ISO → a short readable date; falls back to the raw string when unparseable. */
function formatRequestedOn(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** "Requested on {date} by {name}" — drops whichever part is missing (Flutter shows a "-" for name). */
function requestedMeta(req: MedicationAdminRequestView): string {
  const on = formatRequestedOn(req.requestedOn);
  const by = req.requestedBy ?? '—';
  return on ? `Requested on ${on} by ${by}` : `Requested by ${by}`;
}

export function MedicationAdminRequests({ requests, onGive }: MedicationAdminRequestsProps) {
  const designV2 = useDesignV2();
  // No requests → section hidden (Flutter parity: the whole block is gated on length > 0).
  if (requests.length === 0) return null;

  // Frame v2: the design draws no medication requests; drawn in its language as the vaccine requests are (one Plan card, one
  // block, hairline-separated rows), same Give handler.
  if (designV2) {
    return (
      <EncSectionCard section="p" aria-label="Medication administration requests">
        <EncBlock title="Medication administration requests" icon="pill">
          <div data-slot="pv-stack">
            {requests.map((req) => (
              <div key={req.id} data-slot="pv-row">
                <div data-slot="pv-grow">
                  <div data-slot="pv-title">{req.title || 'Medication'}</div>
                  {req.detail && <p data-slot="pv-meta">{req.detail}</p>}
                  <p data-slot="pv-meta">{requestedMeta(req)}</p>
                </div>
                {onGive && (
                  <Button variant="primary" size="sm" onClick={() => onGive(req)}>
                    Give
                  </Button>
                )}
              </div>
            ))}
          </div>
        </EncBlock>
      </EncSectionCard>
    );
  }

  return (
    <section className={CARD} aria-label="Medication administration requests">
      <EncounterSectionHeader title="Medication Administration Requests" variant="section" />
      <div className="h-3" />
      <div className={STACK_2}>
        {requests.map((req) => (
          <div key={req.id} className={ROW}>
            <div className="min-w-0">
              <div className={TITLE}>{req.title || 'Medication'}</div>
              {req.detail && <p className={DETAIL}>{req.detail}</p>}
              <p className={META}>{requestedMeta(req)}</p>
            </div>
            {onGive && (
              <Button variant="primary" size="sm" onClick={() => onGive(req)}>
                Give
              </Button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
